import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ZMENA_PRECHODY, type ZlSmer, type ZmenaStav } from '@helfr/domain';
import { api, hlaskaZChyby } from '../../api/client.js';
import type {
  HlidacPolozka,
  Vyzva,
  VyzvaAdresat,
  ZmenaDetail,
  ZmenaSeznam,
  ZmenovyList,
} from '../../api/types.js';
import { useAuth } from '../../auth/AuthContext.js';
import { PodpisVstup } from '../../components/PodpisVstup.js';
import { PodpisNahled } from '../../components/PodpisNahled.js';
import { PodpisProtistranyDialog } from '../../components/PodpisProtistranyDialog.js';
import type { Podpis as PodpisTyp } from '../../api/types.js';
import { formatDatum, formatKc } from '../../lib/format.js';
import {
  VYZVA_STAV_BARVA,
  VYZVA_STAV_LABEL,
  ZL_OCENENI_LABEL,
  ZL_STAV_BARVA,
  ZL_STAV_LABEL,
  ZMENA_STAV_BARVA,
  ZMENA_STAV_LABEL,
} from '../../lib/labels.js';
import {
  Badge,
  Btn,
  Callout,
  Chyba,
  Dialog,
  Input,
  Menu,
  Nacitani,
  Napoveda,
  Pole,
  Select,
  Textarea,
} from '../../components/ui.js';
import { PrilohyNalezu } from '../NalezyPage.js';
import { ZlEditor } from './ZlEditor.js';

/** Kroky životního cyklu Změny pro breadcrumb (bez koncových větví zamítnutí/spor). */
const STAVOVE_KROKY: ZmenaStav[] = [
  'kvalifikovana',
  'ocenena',
  'schvalena',
  'v_dodatku',
  'fakturovatelna',
];

/** Věta „další krok" ke každému stavu (UX audit B3.2 — konec hádání, kdy kliknout). */
const DALSI_KROK: Record<ZmenaStav, string> = {
  nova: 'Další krok: kvalifikujte nález na Změnu ve Frontě nálezů.',
  kvalifikovana:
    'Další krok: doplňte ocenění obou stran, pak Změnu označte jako oceněnou (→ oceněna).',
  ocenena: 'Další krok: nechte Změnu schválit (→ schválena).',
  schvalena:
    'Další krok: podepsané výnosové ZL zařaďte do dodatku — stav „v dodatku" vzniká podpisem dodatku.',
  v_dodatku: 'Další krok: podepište dodatek — tím se Změna stane fakturovatelnou.',
  fakturovatelna: 'Hotovo — Změna je fakturovatelná.',
  zamitnuta: 'Změna je zamítnutá.',
  sporna: 'Změna je sporná (claim) — jednáte s investorem o uznání.',
};

type ZlAkce =
  | { typ: 'podepsat'; zl: ZmenovyList }
  | { typ: 'provedeno'; zl: ZmenovyList }
  | { typ: 'zamitnout'; zl: ZmenovyList }
  | { typ: 'editor'; smer: ZlSmer; zl?: ZmenovyList }
  | { typ: 'neseme-sami' }
  | { typ: 'podpis-odkaz'; zl: ZmenovyList };

/**
 * DETAIL ZMĚNY — srdce klínu (wireframe 13 §3): dvě smluvní strany vedle sebe,
 * MARŽE fyzicky uprostřed (layout sám vypráví byznys model). Neoceněná strana
 * = „?", NIKDY 0; záporná marže červeně. Hlídače mluví lidsky a nabízejí akci.
 * Vazby na nálezy a deník jsou read-only důkaz.
 */
export function ZmenaDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [akce, setAkce] = useState<ZlAkce | null>(null);
  const [chybaStavu, setChybaStavu] = useState<string | null>(null);

  const {
    data: zmena,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['zmena', id],
    queryFn: () => api.get<ZmenaDetail>(`/api/zmeny/${id}`),
  });

  const zmenStav = useMutation({
    mutationFn: (stav: ZmenaStav) => api.post<ZmenaDetail>(`/api/zmeny/${id}/stav`, { stav }),
    onSuccess: () => {
      setChybaStavu(null);
      void queryClient.invalidateQueries({ queryKey: ['zmena', id] });
    },
    onError: (err) => setChybaStavu(hlaskaZChyby(err)),
  });

  if (isLoading) return <Nacitani text="Načítám Změnu…" />;
  if (error) return <Chyba>{hlaskaZChyby(error)}</Chyba>;
  if (!zmena) return null;

  const vynosove = zmena.zmenoveListy.filter((zl) => zl.smer === 'vynos');
  const nakladove = zmena.zmenoveListy.filter((zl) => zl.smer === 'naklad');
  const kvalifikovali = [
    zmena.kvalifikovalStavbyvedouci
      ? `${zmena.kvalifikovalStavbyvedouci.jmeno} (stavbyvedoucí)`
      : null,
    zmena.kvalifikovalPripravar ? `${zmena.kvalifikovalPripravar.jmeno} (přípravář)` : null,
  ].filter(Boolean);

  return (
    <>
      {/* Masthead dokumentu: vlevo titul + kontext, vpravo STAV jako razítko
          (prominentně, na očekávaném místě). */}
      <header className="flex flex-col-reverse items-start gap-3 border-b border-border pb-4 sm:flex-row sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text-secondary">
            <Link to="/zmeny" className="underline underline-offset-2">
              Změny
            </Link>{' '}
            / {zmena.cislo ?? zmena.id}
          </div>
          <div className="mt-1 flex items-start gap-2">
            <h1 className="text-xl font-bold lg:text-2xl">
              ZMĚNA {zmena.cislo ?? ''} — {zmena.popis}
              {zmena.objekt ? `, ${zmena.objekt.kod}` : ''}
            </h1>
            {/* Vysvětlení modelu dvou stran + marže — on-demand za „?" (blueprint 16). */}
            <Napoveda titulek="Jak Změna funguje">
              <p>
                Co fakturujeme ↑ investorovi · Co nás to stojí ↓ · Marže = rozdíl. Ocení se{' '}
                <strong>obě strany</strong>; podepsané výnosové ZL se zařadí do dodatku → Změna je
                fakturovatelná.
              </p>
              {kvalifikovali.length > 0 && (
                <p className="mt-1 text-text-secondary">
                  Kvalifikoval: {kvalifikovali.join(' + ')}
                </p>
              )}
            </Napoveda>
          </div>
          <DopadNaTermin zmena={zmena} />
          {zmena.nesemeSami && (
            <div className="mt-1">
              <p className="font-semibold text-warning-text">
                Vědomé rozhodnutí „neseme sami"
                {zmena.nesemeSamiUser ? ` — ${zmena.nesemeSamiUser.jmeno}` : ''}
                {zmena.nesemeSamiDuvod ? ` (${zmena.nesemeSamiDuvod})` : ''}
              </p>
              {(zmena.podpisy ?? [])
                .filter((pod) => pod.entita === 'Zmena')
                .map((pod) => (
                  <div key={pod.id} className="mt-1">
                    <PodpisNahled podpis={pod} />
                  </div>
                ))}
            </div>
          )}
        </div>
        <div
          className={`shrink-0 rounded-xl px-4 py-2 text-center ${ZMENA_STAV_BARVA[zmena.stav]}`}
        >
          <p className="text-xs font-bold tracking-wide uppercase opacity-80">Stav změny</p>
          <p className="text-lg font-bold">{ZMENA_STAV_LABEL[zmena.stav]}</p>
        </div>
      </header>

      {/* 1) Kde jsem + co udělat (akční hero: stepper + jedna primární akce). */}
      <StavovyPruvodce
        stav={zmena.stav}
        prechody={ZMENA_PRECHODY[zmena.stav]}
        probiha={zmenStav.isPending}
        onPrechod={(cil) => zmenStav.mutate(cil)}
        kontextovaAkce={(() => {
          // „schválena": věta říká podepsat/zařadit — tlačítko musí být u ní.
          if (zmena.stav !== 'schvalena') return undefined;
          const kPodpisu = vynosove.find(
            (zl) => zl.stav !== 'podepsano' && zl.stav !== 'zamitnuto',
          );
          if (kPodpisu)
            return {
              text: `Podepsat ${kPodpisu.cislo}`,
              onClick: () => setAkce({ typ: 'podepsat', zl: kPodpisu }),
            };
          const doDodatku = vynosove.find((zl) => zl.stav === 'podepsano' && !zl.dodatekId);
          if (doDodatku)
            return {
              text: 'Zařadit do dodatku…',
              onClick: () =>
                void navigate(`/dodatky?zakazka=${zmena.zakazkaId}&zl=${doDodatku.id}&novy=1`),
            };
          return undefined;
        })()}
      />
      {chybaStavu && <Chyba>{chybaStavu}</Chyba>}

      {/* 2) Urgentní „co vyřešit" hned pod stavem — hlídače s akcí (10 §4). */}
      {zmena.hlidace.map((h, i) => (
        <HlidacPanel key={i} hlidac={h} onAkce={setAkce} />
      ))}

      {/* 3) Jak na tom jsme (peníze) — marže jako souhrnný pruh nad detailem. */}
      <MarzeBox zmena={zmena} />

      {/* 4) Detail: co fakturujeme | co nás to stojí (dvě strany). */}
      <div className="grid gap-4 lg:grid-cols-2">
        <StranaZmeny
          nadpis="Co fakturujeme"
          napoveda="ZL k hlavní SOD (investor) — nahoru, výnos"
          suma={zmena.vynos}
          zl={vynosove}
          podpisy={zmena.podpisy ?? []}
          zakazkaId={zmena.zakazkaId}
          onAkce={setAkce}
          pridatText="+ Založit další výnosový ZL"
          smer="vynos"
        />
        <StranaZmeny
          nadpis="Co nás to stojí"
          napoveda="ZL k subsmlouvám / vlastní kalkulace / ruční náklad — dolů, náklad"
          suma={zmena.naklad}
          zl={nakladove}
          podpisy={zmena.podpisy ?? []}
          zakazkaId={zmena.zakazkaId}
          onAkce={setAkce}
          pridatText="+ Založit nákladový ZL"
          smer="naklad"
        />
      </div>

      {/* 5) Razítko dozoru: výzvy TDI k ZL a podkladovému deníku (E1–E2). */}
      <TdiSekce zmena={zmena} />

      {/* 6) Podklad: nálezy + deníkové vazby — READ-ONLY důkaz z terénu, dole. */}
      {zmena.nalezy.length > 0 && (
        <section className="rounded-xl border border-border bg-white p-4">
          <h2 className="mb-2 font-bold">Podklad (read-only)</h2>
          <ul className="flex flex-col gap-3">
            {zmena.nalezy.map((n) => (
              <li
                key={n.id}
                className="flex flex-col gap-2 border-t border-border pt-2 first:border-t-0 first:pt-0"
              >
                <p className="text-sm text-text-secondary">
                  Nález {n.autor.jmeno} · {formatDatum(n.kdy)}
                  {n.denikZaznam && (
                    <>
                      {' '}
                      ·{' '}
                      <Link
                        to={`/denik/${n.denikZaznam.id}`}
                        className="underline underline-offset-2"
                      >
                        deník {formatDatum(n.denikZaznam.datum)}
                      </Link>
                    </>
                  )}
                </p>
                <p>„{n.popis}"</p>
                <PrilohyNalezu prilohy={n.prilohy ?? []} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-sm text-text-secondary">Částky netto, ceny bez DPH.</p>

      {akce?.typ === 'editor' && (
        <ZlEditor zmena={zmena} smer={akce.smer} zl={akce.zl} onZavrit={() => setAkce(null)} />
      )}
      {akce?.typ === 'podepsat' && (
        <PodepsatZlDialog zmenaId={zmena.id} zl={akce.zl} onZavrit={() => setAkce(null)} />
      )}
      {akce?.typ === 'provedeno' && (
        <ProvedenoDialog zmenaId={zmena.id} zl={akce.zl} onZavrit={() => setAkce(null)} />
      )}
      {akce?.typ === 'zamitnout' && (
        <ZamitnoutZlDialog zmena={zmena} zl={akce.zl} onZavrit={() => setAkce(null)} />
      )}
      {akce?.typ === 'neseme-sami' && (
        <NesemeSamiDialog zmenaId={zmena.id} onZavrit={() => setAkce(null)} />
      )}
      {akce?.typ === 'podpis-odkaz' && (
        <PodpisProtistranyDialog
          entita="ZmenovyList"
          entitaId={akce.zl.id}
          nazevDokumentu={`${akce.zl.cislo ?? 'ZL'} — ${akce.zl.popis}`}
          jmeno={akce.zl.smlouva?.subdodavatel?.nazev ?? akce.zl.smlouva?.protistrana}
          onZavrit={() => setAkce(null)}
        />
      )}
    </>
  );
}

/** Doporučený „další krok" = první nezamčený přechod (na něj je primární akce). */
function primarniPrechod(prechody: readonly ZmenaStav[]): ZmenaStav | undefined {
  return prechody.find((c) => c !== 'v_dodatku' && c !== 'fakturovatelna');
}

/**
 * Průvodce stavem Změny (blueprint 16 — radikální simplifikace): default je
 * JEDNA věta „Další krok: …" + JEDNA dominantní primární akce (doporučený
 * přechod). Celá cesta 5 stavů (breadcrumb) je schovaná za kliknutí na stav —
 * cesta zpět zůstává. Ostatní povolené přechody jsou v „⋯ Další". Nic z funkce
 * nezmizelo, jen z očí.
 */
function StavovyPruvodce({
  stav,
  prechody,
  probiha,
  onPrechod,
  kontextovaAkce,
}: {
  stav: ZmenaStav;
  prechody: readonly ZmenaStav[];
  probiha: boolean;
  onPrechod: (cil: ZmenaStav) => void;
  /** Akce k větě „Další krok" tam, kde další krok NENÍ přechod stavu
      (např. „schválena": podepsat ZL / zařadit do dodatku). */
  kontextovaAkce?: { text: string; onClick: () => void };
}) {
  const aktualniIndex = STAVOVE_KROKY.indexOf(stav);
  const mimoHlavniVetev = aktualniIndex === -1; // zamitnuta / sporna / nova
  const primarni = primarniPrechod(prechody);
  const sekundarni = prechody.filter((c) => c !== primarni);

  return (
    // Akční hero: NAHOŘE „kde jsem" (stepper životního cyklu, vždy vidět = jak
    // to funguje), POD ním „co teď udělat" (věta + JEDNA výrazná primární akce).
    <section
      aria-label="Průvodce stavem Změny"
      className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-4 shadow-sm"
    >
      {!mimoHlavniVetev && (
        <ol className="flex flex-wrap items-center gap-1 text-xs">
          {STAVOVE_KROKY.map((krok, i) => {
            const hotovy = i < aktualniIndex;
            const aktualni = krok === stav;
            return (
              <li key={krok} className="flex items-center gap-1">
                <span
                  className={`rounded-full px-2.5 py-0.5 font-semibold ${
                    aktualni
                      ? 'bg-primary text-white'
                      : hotovy
                        ? 'text-success-text'
                        : 'text-text-muted'
                  }`}
                >
                  {hotovy ? '✓ ' : ''}
                  {ZMENA_STAV_LABEL[krok]}
                </span>
                {i < STAVOVE_KROKY.length - 1 && (
                  <span aria-hidden="true" className="text-text-muted">
                    ›
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <div
        className={`flex flex-wrap items-center justify-between gap-3 ${
          mimoHlavniVetev ? '' : 'border-t border-border pt-3'
        }`}
      >
        <p className="text-base font-semibold text-text-primary">{DALSI_KROK[stav]}</p>
        {kontextovaAkce && (
          <Btn size="desktop" onClick={kontextovaAkce.onClick}>
            {kontextovaAkce.text}
          </Btn>
        )}
        {prechody.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {/* JEDNA dominantní primární akce = doporučený další krok. */}
            {primarni && (
              <Btn size="desktop" disabled={probiha} onClick={() => onPrechod(primarni)}>
                → {ZMENA_STAV_LABEL[primarni]}
              </Btn>
            )}
            {/* Ostatní stavové přechody schované do „⋯ Další" (blueprint 16). */}
            {sekundarni.length > 0 && (
              <Menu stitek="⋯ Další akce" align="left">
                {sekundarni.map((cil) => {
                  const jenDodatkem = cil === 'v_dodatku' || cil === 'fakturovatelna';
                  return (
                    <Btn
                      key={cil}
                      size="desktop"
                      varianta="secondary"
                      disabled={jenDodatkem || probiha}
                      title={jenDodatkem ? 'vzniká podpisem dodatku' : undefined}
                      onClick={() => onPrechod(cil)}
                    >
                      → {ZMENA_STAV_LABEL[cil]}
                      {jenDodatkem ? ' (podpisem dodatku)' : ''}
                    </Btn>
                  );
                })}
              </Menu>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * MARŽE ZMĚNY — souhrnný pruh nad detailem: vlevo „jak to funguje" (výnos −
 * náklad), vpravo velký výsledek. null → „?" (nikdy 0), záporná červeně.
 */
function MarzeBox({ zmena }: { zmena: ZmenaDetail }) {
  const neuplna = zmena.marze == null;
  const zaporna = zmena.marze != null && zmena.marze.startsWith('-');
  return (
    <section
      aria-label="Marže Změny"
      className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 p-5 ${
        neuplna
          ? 'border-info-border bg-info-bg'
          : zaporna
            ? 'border-error-border bg-error-bg'
            : 'border-success-border bg-success-bg'
      }`}
    >
      <div className="ph-mask min-w-0">
        <h2 className="text-sm font-bold tracking-wide text-text-secondary uppercase">
          Marže Změny
        </h2>
        {neuplna ? (
          <p className="mt-1 text-base text-text-secondary">
            {zmena.spornyVynos
              ? 'Výnosová strana je sporná (claim) — marže je nejistá.'
              : 'doplňte ocenění obou stran'}
          </p>
        ) : (
          <p className="mt-1 text-base font-semibold text-text-primary">
            {formatKc(zmena.vynos)} <span className="text-text-secondary">fakturujeme</span> −{' '}
            {formatKc(zmena.naklad)} <span className="text-text-secondary">stojí nás</span>
          </p>
        )}
      </div>
      <div className="text-right">
        {neuplna ? (
          <p className="text-5xl font-bold text-info-text">?</p>
        ) : (
          <>
            <p
              className={`text-4xl font-bold ${zaporna ? 'text-error-text' : 'text-success-text'}`}
            >
              {formatKc(zmena.marze)}
            </p>
            <p
              className={`text-base font-bold ${zaporna ? 'text-error-text' : 'text-success-text'}`}
            >
              {zaporna ? 'ZTRÁTOVÁ' : 'zisk'}
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function StranaZmeny({
  nadpis,
  napoveda,
  suma,
  zl,
  smer,
  zakazkaId,
  pridatText,
  podpisy = [],
  onAkce,
}: {
  nadpis: string;
  /** Vysvětlení strany (dřív trvalý popisek) — teď on-demand za „?". */
  napoveda: string;
  suma: string | null | undefined;
  zl: ZmenovyList[];
  smer: ZlSmer;
  zakazkaId: string;
  pridatText: string;
  podpisy?: PodpisTyp[];
  onAkce: (a: ZlAkce) => void;
}) {
  const { maPravo } = useAuth();
  return (
    <section className="flex flex-col gap-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="font-bold tracking-wide uppercase">{nadpis}</h2>
          <Napoveda titulek={nadpis}>
            <p>
              {napoveda}. <strong>ZL</strong> = změnový list, <strong>SOD</strong> = smlouva o dílo,{' '}
              <strong>CN</strong> = cenová nabídka s rozpadem.
            </p>
          </Napoveda>
        </div>
        <p className="mt-1 font-semibold">
          Σ {smer === 'vynos' ? 'výnos' : 'náklad'}: {formatKc(suma)}
        </p>
      </div>
      {zl.length === 0 && (
        <p className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-text-secondary">
          Zatím neoceněno
        </p>
      )}
      {zl.map((z) => (
        <ZlKarta
          key={z.id}
          zl={z}
          zakazkaId={zakazkaId}
          podpisy={podpisy.filter((pod) => pod.entita === 'ZmenovyList' && pod.entitaId === z.id)}
          onAkce={onAkce}
        />
      ))}
      {maPravo('finance.write') && (
        <Btn size="desktop" varianta="secondary" onClick={() => onAkce({ typ: 'editor', smer })}>
          {pridatText}
        </Btn>
      )}
    </section>
  );
}

/** Karta jednoho ZL: položky (méněpráce červeně), Σ ze serveru, akce dle stavu. */
function ZlKarta({
  zl,
  zakazkaId,
  podpisy = [],
  onAkce,
}: {
  zl: ZmenovyList;
  zakazkaId: string;
  podpisy?: PodpisTyp[];
  onAkce: (a: ZlAkce) => void;
}) {
  const { maPravo } = useAuth();
  const navigate = useNavigate();
  const smiPsat = maPravo('finance.write');
  // Uzavření smyčky k dodatku (B6.1): podepsaný výnosový ZL bez dodatku.
  const lzeDoDodatku = zl.smer === 'vynos' && zl.stav === 'podepsano' && !zl.dodatek;
  const editovatelny = ['navrzeno', 'provedeno_na_riziko', 'sporne'].includes(zl.stav);
  const protistrana = zl.smlouva
    ? (zl.smlouva.subdodavatel?.nazev ?? zl.smlouva.protistrana)
    : ZL_OCENENI_LABEL[zl.oceneniTyp];

  return (
    <article className="rounded-xl border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold">
          {zl.cislo ?? 'ZL'} · {protistrana}
        </h3>
        <span className="flex items-center gap-2">
          <a
            href={`/api/zmeny/${zl.zmenaId}/zl/${zl.id}/pdf`}
            download
            className="text-sm font-semibold text-primary underline underline-offset-2"
          >
            PDF
          </a>
          <Badge barva={ZL_STAV_BARVA[zl.stav]}>{ZL_STAV_LABEL[zl.stav]}</Badge>
        </span>
      </div>
      <p className="text-sm text-text-secondary">{zl.popis}</p>
      {podpisy.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {podpisy.map((pod) => (
            <PodpisNahled key={pod.id} podpis={pod} />
          ))}
        </div>
      )}
      {zl.rizikoProvedenoBezPodpisu && (
        <p className="mt-2 inline-flex items-center gap-1 rounded-md bg-warning-bg px-2 py-1 text-sm font-medium text-warning-text">
          ⚠ práce hotová, ZL nepodepsán → eskalováno stavbyvedoucímu
        </p>
      )}
      {zl.duvodZamitnuti && (
        <p className="mt-1 text-sm text-error-text">
          Důvod {zl.stav === 'sporne' ? 'sporu' : 'zamítnutí'}: {zl.duvodZamitnuti}
          {zl.kompenzovanoVZl && (
            <>
              {' '}
              — kompenzováno v{' '}
              <Link
                to={`/zmeny/${zl.kompenzovanoVZl.zmenaId}`}
                className="underline underline-offset-2"
              >
                {zl.kompenzovanoVZl.cislo ?? 'ZL'}
              </Link>
            </>
          )}
        </p>
      )}

      {zl.polozky.length > 0 && (
        <div className="overflow-x-auto">
          <table className="mt-2 w-full text-sm">
            <caption className="sr-only">Položky {zl.cislo ?? 'ZL'}</caption>
            <tbody>
              {zl.polozky.map((p) => {
                const zaporna = p.mnozstvi.startsWith('-');
                return (
                  <tr key={p.id} className={zaporna ? 'text-error-text' : ''}>
                    <td className="py-1 pr-2">
                      {p.soupisPraciPolozka ? `${p.soupisPraciPolozka.kod} ` : ''}
                      {p.popis}
                      {zaporna ? ' (méněpráce, záporná)' : ''}
                    </td>
                    <td className="py-1 pr-2 text-right whitespace-nowrap">
                      {p.mnozstvi.replace('-', '−')} {p.mj}
                      {p.jednotkovaCena !== undefined && ` × ${formatKc(p.jednotkovaCena)}`}
                    </td>
                    <td className="py-1 text-right font-semibold whitespace-nowrap">
                      {formatKc(p.castka)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {zl.vlastniKalkulace.length > 0 && (
        <div className="overflow-x-auto">
          <table className="mt-2 w-full text-sm">
            <caption className="sr-only">Vlastní kalkulace {zl.cislo ?? 'ZL'}</caption>
            <tbody>
              {zl.vlastniKalkulace.map((r) => (
                <tr key={r.id}>
                  <td className="py-1 pr-2">
                    {r.profese} · {r.hodiny} h
                    {/* Sazba se zobrazí JEN pokud ji server poslal (zeď — invariant 7). */}
                    {r.calcRate?.sazbaKcH !== undefined && ` × ${formatKc(r.calcRate.sazbaKcH)}/h`}
                    {r.materialPopis ? ` + materiál ${r.materialPopis}` : ''}
                  </td>
                  <td className="py-1 text-right font-semibold whitespace-nowrap">
                    {formatKc(r.castka)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-bg px-3 py-2">
        <span className="text-xs font-bold tracking-wide text-text-secondary uppercase">
          Celkem netto
        </span>
        <span className="text-lg font-bold">{formatKc(zl.castka)}</span>
      </div>
      {zl.dodatek && (
        <p className="text-right text-sm text-text-secondary">
          Dodatek{' '}
          <Link to={`/dodatky/${zl.dodatek.id}`} className="underline underline-offset-2">
            {zl.dodatek.cislo ?? ''}
          </Link>
          {zl.dodatek.podepsano ? ' (podepsaný)' : ' (nepodepsaný)'}
        </p>
      )}

      {smiPsat && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {/* Primární akce podle stavu (B3.3). Právě JEDNA plná primární
              (Podepsat ZL); „Upravit" je vizuálně lehčí subtle/link, aby dvě
              tlačítka vedle sebe nesplývala jako rovnocenná (QA polish). */}
          {editovatelny && (
            <Btn
              size="desktop"
              varianta="subtle"
              onClick={() => onAkce({ typ: 'editor', smer: zl.smer, zl })}
            >
              Upravit
            </Btn>
          )}
          {(zl.stav === 'navrzeno' || zl.stav === 'provedeno_na_riziko') && (
            <>
              <Btn size="desktop" onClick={() => onAkce({ typ: 'podepsat', zl })}>
                Podepsat ZL
              </Btn>
              <Btn
                size="desktop"
                varianta="secondary"
                onClick={() => onAkce({ typ: 'podpis-odkaz', zl })}
              >
                Podpis protistrany
              </Btn>
            </>
          )}
          {lzeDoDodatku && (
            <Btn
              size="desktop"
              onClick={() => void navigate(`/dodatky?zakazka=${zakazkaId}&zl=${zl.id}&novy=1`)}
              title="Otevře dodatky s tímto ZL připraveným k zařazení"
            >
              Zařadit do dodatku…
            </Btn>
          )}
          {/* Okrajové akce schované do „⋯ Další" (B3.3). */}
          {zl.stav !== 'zamitnuto' && (
            <Menu stitek="⋯ Další akce" align="left">
              {!zl.provedeno && (
                <Btn
                  size="desktop"
                  varianta="secondary"
                  onClick={() => onAkce({ typ: 'provedeno', zl })}
                >
                  Označit provedeno
                </Btn>
              )}
              <Btn
                size="desktop"
                varianta="danger"
                onClick={() => onAkce({ typ: 'zamitnout', zl })}
              >
                Zamítnout / spor
              </Btn>
            </Menu>
          )}
        </div>
      )}
    </article>
  );
}

/** Oranžový panel hlídače s textem z API a tlačítkem akce (10 §4). */
function HlidacPanel({ hlidac, onAkce }: { hlidac: HlidacPolozka; onAkce: (a: ZlAkce) => void }) {
  const { maPravo } = useAuth();
  if (hlidac.zavaznost === 'tichy') {
    return <p className="text-sm text-text-secondary">{hlidac.text}</p>;
  }
  // Klidný callout, ne barevná zeď: jemné pozadí + barevná levá hrana signalizuje
  // závažnost (červená marže / žlutá ostatní), aniž láme flow dokumentu.
  return (
    <Callout ton={hlidac.typ === 'marze' ? 'error' : 'warning'}>
      <p className="text-base text-text-primary">{hlidac.text}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {hlidac.akce === 'zalozit-protejsek' && (
          <Btn size="desktop" onClick={() => onAkce({ typ: 'editor', smer: 'vynos' })}>
            Založit protějšek
          </Btn>
        )}
        {(hlidac.typ === 'uplnost' || hlidac.typ === 'marze') && maPravo('zmeny.schvalovat') && (
          <Btn size="desktop" varianta="subtle" onClick={() => onAkce({ typ: 'neseme-sami' })}>
            {hlidac.typ === 'marze'
              ? 'Potvrdit ztrátu (ředitel)'
              : 'Vědomě neseme sami (podpis ředitele)'}
          </Btn>
        )}
      </div>
    </Callout>
  );
}

/** Podpis ZL = zámek (invariant 2) — dialog říká, co podpis znamená. */
function PodepsatZlDialog({
  zmenaId,
  zl,
  onZavrit,
}: {
  zmenaId: string;
  zl: ZmenovyList;
  onZavrit: () => void;
}) {
  const queryClient = useQueryClient();
  const { me, maPravo } = useAuth();
  const [obrazek, setObrazek] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [zzvzKonflikt, setZzvzKonflikt] = useState(false);
  const podepsat = useMutation({
    mutationFn: (prolomitZzvz: boolean = false) =>
      api.post<ZmenaDetail>(`/api/zmeny/${zmenaId}/zl/${zl.id}/podepsat`, {
        podpis: obrazek ? { obrazek, jmeno: me?.jmeno ?? '' } : undefined,
        ...(prolomitZzvz ? { prolomitZzvz: true } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zmena', zmenaId] });
      onZavrit();
    },
    onError: (err) => {
      const hlaska = hlaskaZChyby(err);
      setChyba(hlaska);
      setZzvzKonflikt(hlaska.includes('§ 222'));
    },
  });
  return (
    <Dialog nadpis={`Podepsat ${zl.cislo ?? 'ZL'}`} onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <p>
          Podpis = zámek: položky i hlavička ZL ({formatKc(zl.castka)}) budou od podpisu read-only —
          nikým, ani adminem. Oprava se pak dělá zamítnutím/sporem a novým ZL.
        </p>
        <PodpisVstup
          entita="ZmenovyList"
          entitaId={zl.id}
          jmeno={me?.jmeno ?? ''}
          onChange={setObrazek}
        />
        <p className="-mt-2 text-sm text-text-secondary">Podepisuje: {me?.jmeno}</p>
        {zzvzKonflikt && maPravo('zmeny.schvalovat') && (
          <Btn
            size="desktop"
            varianta="secondary"
            disabled={podepsat.isPending || !obrazek}
            onClick={() => podepsat.mutate(true)}
          >
            Prolomit limit § 222 vědomě (podpis ředitele)
          </Btn>
        )}
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={podepsat.isPending || !obrazek}
            onClick={() => podepsat.mutate(false)}
          >
            {podepsat.isPending ? 'Podepisuji…' : 'Podepsat a uzamknout'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** „Provedeno" — příznak s eskalací, ne blokace; dialog upozorní na riziko. */
function ProvedenoDialog({
  zmenaId,
  zl,
  onZavrit,
}: {
  zmenaId: string;
  zl: ZmenovyList;
  onZavrit: () => void;
}) {
  const queryClient = useQueryClient();
  const [chyba, setChyba] = useState<string | null>(null);
  const oznacit = useMutation({
    mutationFn: () =>
      api.post<ZmenaDetail>(`/api/zmeny/${zmenaId}/zl/${zl.id}/oznacit-provedeno`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zmena', zmenaId] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });
  return (
    <Dialog nadpis={`Označit ${zl.cislo ?? 'ZL'} jako provedený`} onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        {zl.stav !== 'podepsano' && (
          <p className="font-semibold text-warning-text">
            ⚠ ZL není podepsaný — provedení bez podpisu je rizikový stav (hrozí neuznání). Eskaluje
            se stavbyvedoucímu; práci to neblokuje, papír dotáhněte co nejdřív.
          </p>
        )}
        <p>Práce je hotová v terénu? Označení provedeno je nezávislý příznak, jde kdykoli.</p>
        <div className="flex gap-3">
          <Btn size="desktop" disabled={oznacit.isPending} onClick={() => oznacit.mutate()}>
            {oznacit.isPending ? 'Ukládám…' : 'Označit provedeno'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** Zamítnutí / spor ZL: povinný důvod, volba sporné (claim), kompenzace v ZL. */
function ZamitnoutZlDialog({
  zmena,
  zl,
  onZavrit,
}: {
  zmena: ZmenaDetail;
  zl: ZmenovyList;
  onZavrit: () => void;
}) {
  const queryClient = useQueryClient();
  const [duvod, setDuvod] = useState('');
  const [sporne, setSporne] = useState(false);
  const [kompenzovanoVZlId, setKompenzovanoVZlId] = useState('');
  const [chyba, setChyba] = useState<string | null>(null);

  // Kandidáti kompenzace: ZL ostatních Změn zakázky (hlídač marže jinak lže u obou).
  const { data: zmeny } = useQuery({
    queryKey: ['zmeny', zmena.zakazkaId],
    queryFn: () => api.get<ZmenaSeznam[]>(`/api/zmeny?zakazkaId=${zmena.zakazkaId}`),
  });
  const kandidati = (zmeny ?? []).flatMap((z) =>
    z.zmenoveListy
      .filter((k) => k.id !== zl.id)
      .map((k) => ({ id: k.id, popisek: `${k.cislo ?? k.id} (Změna ${z.cislo ?? ''})` })),
  );

  const zamitnout = useMutation({
    mutationFn: () =>
      api.post<ZmenaDetail>(`/api/zmeny/${zmena.id}/zl/${zl.id}/zamitnout`, {
        duvod,
        sporne,
        ...(kompenzovanoVZlId && { kompenzovanoVZlId }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zmena', zmena.id] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  return (
    <Dialog nadpis={`Zamítnout / spor ${zl.cislo ?? 'ZL'}`} onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <Pole popisek="Důvod zamítnutí / sporu" povinne>
          <Textarea
            aria-label="Důvod zamítnutí ZL"
            rows={3}
            value={duvod}
            onChange={(e) => setDuvod(e.target.value)}
          />
        </Pole>
        <label className="flex min-h-11 items-center gap-3">
          <input
            type="checkbox"
            className="size-5"
            checked={sporne}
            onChange={(e) => setSporne(e.target.checked)}
          />
          <span>
            Sporné (claim) — investor práci neuznává, jednáme; Změna se označí jako sporná.
          </span>
        </label>
        <Pole popisek="Kompenzováno v ZL (volitelné)">
          <Select
            aria-label="Kompenzováno v ZL"
            value={kompenzovanoVZlId}
            onChange={(e) => setKompenzovanoVZlId(e.target.value)}
          >
            <option value="">(bez kompenzace)</option>
            {kandidati.map((k) => (
              <option key={k.id} value={k.id}>
                {k.popisek}
              </option>
            ))}
          </Select>
        </Pole>
        <p className="text-sm text-text-secondary">
          Náklad zamítnutého výnosu zůstává — bez vazby na kompenzační ZL by hlídač marže lhal u
          obou změn.
        </p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            varianta="danger"
            disabled={zamitnout.isPending || duvod.trim().length < 3}
            onClick={() => zamitnout.mutate()}
          >
            {zamitnout.isPending ? 'Ukládám…' : sporne ? 'Označit jako sporné' : 'Zamítnout ZL'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** „Vědomě neseme sami" — podpis ředitele (zmeny.schvalovat), důvod povinný. */
function NesemeSamiDialog({ zmenaId, onZavrit }: { zmenaId: string; onZavrit: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [duvod, setDuvod] = useState('');
  const [obrazek, setObrazek] = useState<string | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const rozhodnout = useMutation({
    mutationFn: () =>
      api.post<ZmenaDetail>(`/api/zmeny/${zmenaId}/rozhodnout-neseme-sami`, {
        duvod,
        podpis: obrazek ? { obrazek, jmeno: me?.jmeno ?? '' } : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zmena', zmenaId] });
      onZavrit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });
  return (
    <Dialog nadpis="Vědomě neseme sami (podpis ředitele)" onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        {chyba && <Chyba>{chyba}</Chyba>}
        <p>
          Náklad bez výnosu je v pořádku jen jako vědomé rozhodnutí — zapíše se kdo, kdy a proč
          (auditovatelné) a hlídač úplnosti u téhle Změny zmlkne.
        </p>
        <Pole popisek="Důvod rozhodnutí" povinne>
          <Textarea
            aria-label="Důvod rozhodnutí neseme sami"
            rows={3}
            value={duvod}
            onChange={(e) => setDuvod(e.target.value)}
          />
        </Pole>
        <PodpisVstup
          entita="Zmena"
          entitaId={zmenaId}
          jmeno={me?.jmeno ?? ''}
          onChange={setObrazek}
        />
        <p className="-mt-2 text-sm text-text-secondary">Podepisuje: {me?.jmeno}</p>
        <div className="flex gap-3">
          <Btn
            size="desktop"
            disabled={rozhodnout.isPending || duvod.trim() === '' || !obrazek}
            onClick={() => rozhodnout.mutate()}
          >
            {rozhodnout.isPending ? 'Zapisuji…' : 'Potvrdit rozhodnutí'}
          </Btn>
          <Btn size="desktop" varianta="secondary" onClick={onZavrit}>
            Zrušit
          </Btn>
        </div>
      </div>
    </Dialog>
  );
}

/** Dopad Změny na termín Díla — ZL řeší cenu A čas (nárok na prodloužení). */
function DopadNaTermin({ zmena }: { zmena: ZmenaDetail }) {
  const { maPravo } = useAuth();
  const queryClient = useQueryClient();
  const [edituji, setEdituji] = useState(false);
  const [dny, setDny] = useState(String(zmena.dopadNaTerminDny ?? ''));
  const uloz = useMutation({
    mutationFn: () =>
      api.patch<ZmenaDetail>(`/api/zmeny/${zmena.id}`, {
        dopadNaTerminDny: dny.trim() === '' ? null : Number(dny),
      }),
    onSuccess: (novy) => {
      queryClient.setQueryData(['zmena', zmena.id], novy);
      setEdituji(false);
    },
  });
  return (
    <p className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
      Dopad na termín:{' '}
      {edituji ? (
        <span className="inline-flex items-center gap-1">
          <input
            aria-label="Dopad na termín ve dnech"
            inputMode="numeric"
            className="w-16 rounded-md border border-border px-2 py-0.5 text-right text-sm"
            value={dny}
            onChange={(e) => setDny(e.target.value)}
          />
          dní
          <Btn
            size="desktop"
            varianta="secondary"
            disabled={uloz.isPending}
            onClick={() => uloz.mutate()}
          >
            OK
          </Btn>
        </span>
      ) : (
        <span className="font-semibold text-text-primary">
          {zmena.dopadNaTerminDny ? `+${zmena.dopadNaTerminDny} dní` : 'bez dopadu'}
        </span>
      )}
      {!edituji && maPravo('finance.write') && (
        <button
          type="button"
          className="font-semibold text-primary underline underline-offset-2"
          onClick={() => {
            setDny(String(zmena.dopadNaTerminDny ?? ''));
            setEdituji(true);
          }}
        >
          upravit
        </button>
      )}
    </p>
  );
}

/**
 * Vyjádření TDI ke Změně — „má na to změna razítko dozoru?" na jednom místě:
 * výzvy na ZL Změny + na deníkové záznamy podkladu. Vyžádání i zaevidování
 * vyjádření přímo tady (v POC eviduje kancelář za TDI).
 */
function TdiSekce({ zmena }: { zmena: ZmenaDetail }) {
  const [zadam, setZadam] = useState(false);
  const vyzvy = zmena.vyzvy ?? [];
  const zlCisla = new Map(zmena.zmenoveListy.map((zl) => [zl.id, zl.cislo]));

  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="font-bold">
          Vyjádření TDI{' '}
          <Napoveda titulek="K čemu je vyjádření TDI">
            Podepsaný ZL je dohoda s protistranou; vyjádření technického dozoru je razítko, že práce
            byla potřebná a provedená. Při sporu o proplacení je to klíčový důkaz — vyžádejte ho,
            dokud je co ukázat.
          </Napoveda>
        </h2>
        {zmena.zmenoveListy.length > 0 && (
          <Btn size="desktop" varianta="secondary" onClick={() => setZadam(true)}>
            Vyžádat vyjádření
          </Btn>
        )}
      </div>

      {vyzvy.length === 0 && (
        <p className="text-sm text-text-secondary">
          Zatím žádná výzva — dozor se ke změně nevyjádřil.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {vyzvy.map((v) => (
          <li key={v.id} className="rounded-lg border border-border p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold">
                  {v.predmetTyp === 'zmenovy_list'
                    ? (zlCisla.get(v.predmetId) ?? 'ZL')
                    : 'Deníkový záznam'}
                  <span className="font-normal text-text-secondary">
                    {' '}
                    ·{' '}
                    {v.adresatUser?.jmeno ??
                      (v.adresatRole === 'tdi' ? 'TDI' : v.adresatRole) ??
                      '—'}
                    {v.termin ? ` · termín ${formatDatum(v.termin)}` : ''}
                  </span>
                </p>
                <p className="text-sm whitespace-pre-wrap">{v.text}</p>
              </div>
              <Badge barva={VYZVA_STAV_BARVA[v.stav]}>{VYZVA_STAV_LABEL[v.stav]}</Badge>
            </div>
            {v.vyjadreni ? (
              <p className="mt-2 rounded-lg bg-success-bg p-2 text-sm">
                Vyjádření ({formatDatum(v.vyjadrenoAt)}): {v.vyjadreni}
              </p>
            ) : (
              <TdiVyjadreniForm vyzvaId={v.id} zmenaId={zmena.id} />
            )}
          </li>
        ))}
      </ul>

      {zadam && <VyzadatVyjadreniDialog zmena={zmena} onZavrit={() => setZadam(false)} />}
    </section>
  );
}

/** Zaevidování vyjádření TDI přímo v detailu Změny (E2: jméno + text + datum). */
function TdiVyjadreniForm({ vyzvaId, zmenaId }: { vyzvaId: string; zmenaId: string }) {
  const queryClient = useQueryClient();
  const [oteviram, setOteviram] = useState(false);
  const [jmeno, setJmeno] = useState('');
  const [text, setText] = useState('');
  const odeslat = useMutation({
    mutationFn: () =>
      api.post<Vyzva>(`/api/vyzvy/${vyzvaId}/vyjadreni`, {
        vyjadreni: text,
        ...(jmeno.trim() ? { jmeno: jmeno.trim() } : {}),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['zmena', zmenaId] }),
  });

  if (!oteviram) {
    return (
      <button
        type="button"
        className="mt-1 text-sm font-semibold text-primary underline underline-offset-2"
        onClick={() => setOteviram(true)}
      >
        Zaevidovat vyjádření (za TDI)
      </button>
    );
  }
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Input
        aria-label="Kdo se vyjádřil"
        placeholder="Kdo se vyjádřil — např. Ing. Beneš (TDI)"
        value={jmeno}
        onChange={(e) => setJmeno(e.target.value)}
      />
      <Textarea
        aria-label="Vyjádření TDI"
        rows={3}
        placeholder="Text vyjádření (z mailu / papíru)…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {odeslat.error ? <Chyba>{hlaskaZChyby(odeslat.error)}</Chyba> : null}
      <div>
        <Btn
          size="desktop"
          disabled={!text.trim() || odeslat.isPending}
          onClick={() => odeslat.mutate()}
        >
          {odeslat.isPending ? 'Ukládám…' : 'Zaevidovat vyjádření'}
        </Btn>
      </div>
    </div>
  );
}

/** Výzva TDI k vyjádření ke konkrétnímu ZL — zakládá kancelář z detailu Změny. */
function VyzadatVyjadreniDialog({ zmena, onZavrit }: { zmena: ZmenaDetail; onZavrit: () => void }) {
  const queryClient = useQueryClient();
  const prvniZl = zmena.zmenoveListy[0];
  const [zlId, setZlId] = useState(prvniZl?.id ?? '');
  const [adresatId, setAdresatId] = useState('');
  const [text, setText] = useState('');
  const [termin, setTermin] = useState('');

  const { data: adresati } = useQuery({
    queryKey: ['vyzvy-adresati'],
    queryFn: () => api.get<VyzvaAdresat[]>('/api/vyzvy/adresati'),
  });

  const vybranyZl = zmena.zmenoveListy.find((zl) => zl.id === zlId);
  const vychoziText = vybranyZl
    ? `Žádáme o vyjádření ke změnovému listu ${vybranyZl.cislo} — ${vybranyZl.popis}.`
    : '';

  const odeslat = useMutation({
    mutationFn: () =>
      api.post<Vyzva>('/api/vyzvy', {
        predmetTyp: 'zmenovy_list',
        predmetId: zlId,
        adresatUserId: adresatId || null,
        adresatRole: adresatId ? null : 'tdi',
        text: text.trim() || vychoziText,
        ...(termin ? { termin } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zmena', zmena.id] });
      onZavrit();
    },
  });

  return (
    <Dialog
      nadpis="Vyžádat vyjádření TDI"
      onZavrit={onZavrit}
      akce={
        <Btn size="desktop" disabled={!zlId || odeslat.isPending} onClick={() => odeslat.mutate()}>
          {odeslat.isPending ? 'Odesílám…' : 'Odeslat výzvu'}
        </Btn>
      }
    >
      <div className="flex flex-col gap-3">
        <Pole popisek="K čemu se má TDI vyjádřit">
          <Select value={zlId} onChange={(e) => setZlId(e.target.value)}>
            {zmena.zmenoveListy.map((zl) => (
              <option key={zl.id} value={zl.id}>
                {zl.cislo} — {zl.popis}
              </option>
            ))}
          </Select>
        </Pole>
        <Pole popisek="Komu">
          <Select value={adresatId} onChange={(e) => setAdresatId(e.target.value)}>
            <option value="">TDI (bez konkrétní osoby)</option>
            {(adresati ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.jmeno}
                {a.role.length ? ` (${a.role.join(', ')})` : ''}
              </option>
            ))}
          </Select>
        </Pole>
        <Pole popisek="Text výzvy">
          <Textarea
            rows={3}
            value={text || vychoziText}
            onChange={(e) => setText(e.target.value)}
          />
        </Pole>
        <Pole popisek="Termín vyjádření (nepovinné)">
          <Input type="date" value={termin} onChange={(e) => setTermin(e.target.value)} />
        </Pole>
        {odeslat.error ? <Chyba>{hlaskaZChyby(odeslat.error)}</Chyba> : null}
      </div>
    </Dialog>
  );
}
